import { where, QueryConstraint } from 'firebase/firestore'
import { useTenant, isMultiTenant } from '~/composables/useTenant'

/**
 * Returns the value to write into the `tenantId` field on Firestore docs and
 * Storage customMetadata. This is the Identity Platform tenant ID (not the
 * slug) so that Firestore rules' comparison against
 * request.auth.token.firebase.tenant succeeds.
 */
function tenantIdValue(): string {
  return useTenant().value.identityPlatformTenantId
}

/**
 * Stamps tenantId onto a Firestore doc payload when running in multi mode.
 * In single mode returns data untouched (LZ100/SJMUZ docs stay tenant-free).
 */
export function stampTenantId<T extends Record<string, any>>(data: T): T {
  if (!isMultiTenant()) return data
  const tenantId = tenantIdValue()
  // Never stamp an empty tenantId. If the tenant has not resolved yet, returning
  // the payload untouched fails closed (a create rejected by writesOwnTenant) but
  // crucially a merge-write can no longer overwrite an existing good tenantId with
  // '' — which would silently drop the doc from tenant-scoped queries.
  if (!tenantId) return data
  return { ...data, tenantId }
}

/**
 * Returns query constraints that scope a Firestore query to the current tenant
 * in multi mode. In single mode returns [] — queries run unchanged.
 */
export function tenantScopeConstraints(): QueryConstraint[] {
  if (!isMultiTenant()) return []
  return [where('tenantId', '==', tenantIdValue())]
}

/**
 * Storage customMetadata with tenantId stamped in multi mode.
 */
export function tenantScopedMetadata(extra: Record<string, string> = {}): Record<string, string> {
  if (!isMultiTenant()) return extra
  return { ...extra, tenantId: tenantIdValue() }
}

/**
 * Extra payload to pass on every callable-function invocation so the server can
 * route email links, URLs, and SparkPost substitutions to the right tenant.
 * Empty object in single mode — LZ100 callables continue to rely on env vars.
 */
export function tenantCallPayload(): { tenantId?: string; appUrl?: string; appName?: string } {
  if (!isMultiTenant()) return {}
  const t = useTenant().value
  return {
    tenantId: t.identityPlatformTenantId,
    appUrl: window.location.origin,
    appName: t.appName,
  }
}
